'use client'

import { LogOut, User } from 'lucide-react'
import { Session } from 'next-auth'
import { signOut } from 'next-auth/react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useState } from 'react'

import { Person } from '@/lib/types/auth/profile'

import NavBarLinks from './NavBarLinks' // Import the NavBarLinks component
import NotificationWrapper from './notification-components/NotificationWrapper'
import RecaptchaModal from './RecaptchaModal'
import Button from './ui/CustomButton'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import UserInfo from './UserInfo'

const Header = ({ profile, session }: { profile?: Person; session?: Session }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isRecaptchaOpen, setIsRecaptchaOpen] = useState(false)
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null)
  const router = useRouter()

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  const handleRegisterClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setRecaptchaError(null)
    setIsRecaptchaOpen(true)
  }

  const handleRecaptchaVerify = () => {
    setIsRecaptchaOpen(false)
    // Navigate to signup page after successful verification
    setTimeout(() => {
      router.push('/auth/signup')
    }, 150)
  }

  const handleRecaptchaError = () => {
    setRecaptchaError('Verification failed. Please try again.')
    setIsRecaptchaOpen(false)
    // Show error message for 3 seconds
    setTimeout(() => setRecaptchaError(null), 3000)
  }

  const handleRecaptchaClose = () => {
    setIsRecaptchaOpen(false)
  }

  const user = profile
  if (!user) {
    console.error('User not found', user)
  }

  const logout = async () => {
    await signOut({ redirect: true, callbackUrl: '/' })
  }

  return (
    <>
      <header
        className={`
          flex justify-between items-center w-full
    px-3 py-6
    sm:px-6
    md:px-4 md:py-7 max-w-4xl md:max-w-4xl lg:max-w-5xl xl:max-w-7xl mx-auto
        `}
      >
        <div className='flex w-full items-center justify-between md:w-auto md:justify-start'>
          <button
            onClick={toggleSidebar}
            className='order-2 md:hidden' // Hide the hamburger button on larger screens
          >
            <Image
              src='/icons/hamburgerNew.png'
              alt='Hamburger Menu'
              height={32}
              width={32}
              className={`
              h-10 w-10
            `}
            />
          </button>
          <div className='order-1 flex items-center'>
            <Link href={'/'}>
              <Image
                src='/icons/gowello.svg'
                alt='logo'
                height={47}
                width={193}
                className='h-auto w-[180px] md:w-[193px]'
                // Removed margin
              />
            </Link>
          </div>
        </div>

        {/* Center Navigation */}
        <nav className='hidden md:flex items-center gap-8'>
          <Link href={'/'} className='text-gray-700 hover:text-[#F59405] transition-colors'>
            Home
          </Link>
          <Link href={'/clinic/cliniclisting'} className='text-gray-700 hover:text-[#F59405] transition-colors'>
            Clinics
          </Link>
          <Link href={'/clinic/prescriberslisting'} className='text-gray-700 hover:text-[#F59405] transition-colors'>
            Prescribers
          </Link>
          <Link href={'/about-us'} className='text-gray-700 hover:text-[#F59405] transition-colors'>
            About Us
          </Link>
        </nav>
        <div
          className={`
            hidden items-center gap-4

            md:flex
          `}
        >
          {/* <Link href={'/clinic/clinicReg'}>
            <p
              className={`
                rounded-md border-2 border-[#F59405] bg-[#F59405] px-6 py-1
                text-white
              `}
            >
              Register your Clinic
            </p>
          </Link> */}
          {profile ? (
            <div>
              {session && <NotificationWrapper session={session} />}
              <Popover>
                <PopoverTrigger>
                  <UserInfo
                    profile_picture_display={profile.profile_picture_display || '/icons/nouser.jpg'}
                    name={profile.name}
                    email={profile.user.email}
                  />
                </PopoverTrigger>
                <PopoverContent className='rounded-md p-2'>
                  <div className='flex w-full flex-col items-start'>
                    <Link
                      href={'/clinic/account/personalDetail'}
                      className={`
                        flex w-full items-center gap-2 rounded-sm p-2 text-start
                        transition-all

                        hover:bg-gray-100
                      `}
                    >
                      <User className='w-5 h-w-5' />
                      Profile
                    </Link>
                    <button
                      className={`
                        flex w-full items-center gap-2 rounded-md p-2 text-start
                        transition-all

                        hover:bg-gray-100
                      `}
                      onClick={logout}
                    >
                      <LogOut className='w-5 h-w-5' />
                      Logout
                    </button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          ) : (
            <div className='flex items-center gap-4'>
              <Link href={'/auth/login'}>
                <button
                  className={`
                    rounded-md border-2 border-[#F59405] px-6 py-1

                    hover:bg-gray-100
                  `}
                >
                  Login
                </button>
              </Link>
              <button
                onClick={handleRegisterClick}
                className={`
                  rounded-md bg-[#F59405] px-4 py-[6px] text-center text-white

                  hover:bg-[#E58904]
                `}
              >
                Register
              </button>
            </div>
          )}

          {/* Error message - Responsive positioning */}
          {recaptchaError && (
            <div
              className={`
              fixed z-[150]
              top-20 right-4
              md:absolute md:right-4 md:top-20
              rounded-md bg-red-100 px-4 py-2 text-sm text-red-700 shadow-lg
              max-w-xs
            `}
            >
              {recaptchaError}
            </div>
          )}
        </div>
      </header>

      {/* Sidebar */}
      <div
        className={`
          fixed left-0 top-0 z-[100] h-full w-64 transform bg-white shadow-lg

          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}

          transition-transform duration-300
        `}
      >
        <div className='flex flex-row justify-between p-3'>
          <Link href={'/'}>
            <Image
              src='/icons/gowello.svg'
              alt='logo'
              height={30}
              width={130}
              // Removed margin
            />
          </Link>
          <button onClick={toggleSidebar}>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
              className='h-5 w-5'
            >
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
        </div>
        <div className='flex flex-col justify-between gap-4'>
          <nav className='p-4'>
            <NavBarLinks hiddenLinks={['about-us', 'contact', 'address']} showClinicPatientLinks={Boolean(session)} />
          </nav>
          {profile ? (
            <div
              className={`
                mb-16 flex flex-col items-center gap-4 px-4

                md:hidden
              `}
            >
              <Link
                href={'/clinic/account/personalDetail'}
                className={`
                  flex w-full items-center gap-2 rounded-sm p-2 text-start
                  transition-all

                  hover:bg-gray-100
                `}
              >
                {' '}
                <UserInfo
                  profile_picture_display={profile.profile_picture_display || '/icons/nouser.jpg'}
                  name={profile.name}
                  email={profile.user.email}
                />
              </Link>
              <Button
                label='Logout'
                className={`
                  flex w-full items-center gap-2 rounded-md p-2 text-start
                  transition-all

                  hover:bg-gray-100
                `}
                onClick={logout}
              >
                Logout
              </Button>
            </div>
          ) : (
            <div
              className={`
                flex flex-col gap-1 px-6

                md:hidden
              `}
            >
              <Link href={'/clinic/clinicReg'}>
                <p
                  className={`
                    rounded-md border-2 border-[#F59405] bg-[#F59405] px-6 py-1
                    text-white
                  `}
                >
                  Register your Clinic
                </p>
              </Link>
              <div
                className={`
                  flex w-full flex-row gap-3 py-4

                  md:hidden
                `}
              >
                <Link href={'/auth/login'}>
                  <button
                    className={`
                      w-full rounded-md border-2 border-[#F59405] px-6 py-1

                      hover:bg-gray-100
                    `}
                    onClick={toggleSidebar}
                  >
                    Login
                  </button>
                </Link>
                <button
                  onClick={(e) => {
                    toggleSidebar()
                    handleRegisterClick(e)
                  }}
                  className={`
                    w-full rounded-md bg-[#F59405] px-4 py-[6px] text-center
                    text-white

                    hover:bg-[#E58904]
                  `}
                >
                  Register
                </button>
              </div>
            </div>
          )}

          {/* Error message for mobile sidebar */}
          {recaptchaError && (
            <div className='mx-6 mt-2 rounded-md bg-red-100 px-4 py-2 text-sm text-red-700'>{recaptchaError}</div>
          )}
        </div>
      </div>

      {/* Overlay */}
      {isSidebarOpen && <div className='fixed inset-0 z-[50] bg-black opacity-50' onClick={toggleSidebar}></div>}

      {/* reCAPTCHA Modal */}
      <RecaptchaModal
        isOpen={isRecaptchaOpen}
        onClose={handleRecaptchaClose}
        onVerify={handleRecaptchaVerify}
        onError={handleRecaptchaError}
      />
    </>
  )
}

export default Header
